import { useCallback, useEffect, useMemo, useState } from 'react';
import { flatMap, keyBy, keys, values } from 'lodash';
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Flex, Link, Avatar, useDisclosure } from '@chakra-ui/react';

import { MultiSelectOption } from 'components/atoms/AMultiSelect';
import { useLazyGetSectoListsQuery } from 'core/API/sectoApi';
import { useGetUsersQuery } from 'core/API/usersApi';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { filtreAndOrderColumns } from 'shared/table';
import { useRetailers } from 'core/redux/selectors/dataSelectors';

import { BASE_SECTO, BDD_COLUMNS, LIST_FIELDS } from './const';
import { ISecto, ISectoViewData } from './types';

type TSectoProps = { toolId: number };

const AvatarCell = (name: string) => {
    return <Flex>{name && <Avatar size="xs" bg="teal.500" color="white" ml={2} name={name} />}</Flex>;
};

const useSectoContainer = ({ toolId }: TSectoProps) => {
    const translate = useTranslation();
    const [selectedCustom, setSelectedCustoms] = useState<Record<string, { name: string; users: MultiSelectOption[] }>>(
        {},
    );

    const [options, setOptions] = useState<MultiSelectOption[]>([]);
    const [selectedRetailers, setSelectedRetailers] = useState<(string | number)[]>([]);
    const [selectedHiddenHeader, setSelectedHiddenHeader] = useState<Record<string, boolean>>({});
    const { retailers: retailersData, isLoading: isRetailerLoading, isError: isRetailerError } = useRetailers();
    const retailersById = keyBy(retailersData, 'id');

    const [lists, setLists] = useState<Record<string, any[]>>({});

    const { data: users, isError: isUserError, isLoading: isUserLoading } = useGetUsersQuery();
    const [getGetSectoListsQuery, { isLoading: isSectoListLoading, isFetching: isLoading, isError }] =
        useLazyGetSectoListsQuery();

    const { isOpen, onOpen, onClose } = useDisclosure();

    const [sectoData, setListsData] = useState<ISecto[]>([]);
    const [sectoDataView, setListsDataView] = useState<ISectoViewData[]>([]);
    const [selectedSecto, setSelectedSectoData] = useState<ISecto>(BASE_SECTO);

    const headersToHide = useMemo(
        () =>
            [
                ...Object.keys(selectedHiddenHeader).map((header) => {
                    if (selectedHiddenHeader[header]) return header;
                    return '';
                }),
                'customs',
            ].filter((header) => header !== ''),
        [selectedHiddenHeader],
    );

    useEffect(() => {
        if (lists && !isLoading && !isError) {
            const concatList = flatMap(values(lists));
            const formattedData = concatList.map((shop) => {
                const newShop = { ...shop } as ISectoViewData;
                if (newShop.customs) {
                    Object.keys(newShop.customs).forEach((custom) => {
                        newShop[BDD_COLUMNS[custom as keyof typeof BDD_COLUMNS]] = {
                            users: newShop?.customs[custom]?.users,
                            name: newShop?.customs[custom]?.name,
                        };
                    });
                }
                return newShop;
            });
            setListsData(concatList);
            setListsDataView(formattedData);
        }
    }, [isError, isLoading, lists]);

    useEffect(() => {
        if (!isUserError && !isUserLoading && users) {
            setOptions(
                users.users.map((user) => {
                    return { id: user.id, name: `${user.first_name} ${user.last_name}` };
                }),
            );
        }
    }, [isUserError, isUserLoading, users]);

    const handleLists = async (retailerId: number) => {
        const resp = await getGetSectoListsQuery({ idTool: toolId, retailerId });
        setLists((list) => ({ ...list, [retailerId]: resp.data?.lists }));
    };

    useEffect(() => {
        const retailers = keys(lists);
        const added = selectedRetailers.find((x) => !retailers.includes(`${x}`));
        const removed = retailers.filter((x) => !selectedRetailers.includes(Number(x)));

        if (added) {
            setLists((list) => ({ ...list, [Number(added)]: [] }));
            handleLists(Number(added));
        }

        if (removed.length) {
            setLists((list) => {
                const newList = { ...list };
                removed.forEach((id) => delete newList[id]);
                return newList;
            });
        }
    }, [selectedRetailers]);

    const handleOpenEditAddModal = useCallback(
        (refId: number) => {
            const selectedSectoData = sectoData.find((secto) => secto.shop_id === refId);
            setSelectedCustoms({});
            if (selectedSectoData) {
                const newSelectedCustom: Record<string, { name: string; users: MultiSelectOption[] }> = {};
                if (selectedSectoData.customs) {
                    Object.keys(selectedSectoData.customs).forEach((key) => {
                        const custom = selectedSectoData.customs[key];
                        newSelectedCustom[key] = { ...custom };
                    });
                    setSelectedCustoms(newSelectedCustom);
                }
                setSelectedSectoData(selectedSectoData);
            }
            onOpen();
        },
        [sectoData, selectedSecto],
    );

    const columns: ColumnDef<ISectoViewData, any>[] = useMemo(() => {
        const filteredTableHeader: ColumnDef<ISectoViewData, any>[] = filtreAndOrderColumns(
            LIST_FIELDS,
            LIST_FIELDS,
            headersToHide,
        ).map((header: string): ColumnDef<ISectoViewData, any> => {
            if (header.includes('custom_')) {
                return {
                    header: () => translate(`secto.column.${header}`) as string,
                    id: header,
                    accessorFn: (row) =>
                        `${row[header]?.name || ''} ${
                            row[header]?.users?.map((user: any) => user.name).join(' ') || ''
                        }`,
                    cell: (info) => {
                        const { original } = info.row;
                        return (
                            <Flex whiteSpace="nowrap">
                                {original[header]?.name}
                                {original[header]?.users?.map((manager: any) => AvatarCell(manager.name))}
                            </Flex>
                        );
                    },
                } as ColumnDef<ISectoViewData, any>;
            }
            switch (header) {
                case 'retailer':
                    return {
                        header: () => translate(`secto.column.${header}`) as string,
                        id: header,
                        accessorFn: (row) => retailersById[row.retailer_id]?.name || '',
                        cell: (info) => info.getValue(),
                    } as ColumnDef<ISectoViewData, any>;
                case 'shop_id':
                    return {
                        header: () => translate(`secto.column.${header}`) as string,
                        accessorKey: header,
                        meta: {
                            minWidth: '200px',
                            width: '200px',
                        },
                        cell: (info) => {
                            return (
                                <Flex>
                                    <Link onClick={() => handleOpenEditAddModal(info.getValue())} color="blue.500">
                                        {info.getValue()}
                                    </Link>
                                </Flex>
                            );
                        },
                    };
                default:
                    return {
                        header: () => translate(`secto.column.${header}`) as string,
                        accessorKey: header,
                        meta: {
                            minWidth: '200px',
                            width: '200px',
                        },
                        cell: (info) => {
                            return <Flex>{info.getValue()}</Flex>;
                        },
                    };
            }
        });

        return filteredTableHeader;
    }, [sectoDataView, headersToHide]);

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [sectoDataView, headersToHide],
    );

    const table = useReactTable<ISectoViewData>({
        data: sectoDataView,
        columns,
        defaultColumn,
        sortingFns: SORTING_FNS,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const props = useMemo(
        () => ({
            isLoading,
            table,
            selectedSecto,
            selectedCustom,
            options,
            isOpen,
            sectoDataView,
            selectedRetailers,
            selectedHiddenHeader,
            setSelectedHiddenHeader,
            setSelectedRetailers,
            setSelectedSectoData,
            setSelectedCustoms,
            handleOpenEditAddModal,
            onClose,
        }),
        [
            isLoading,
            table,
            sectoDataView,
            selectedSecto,
            selectedCustom,
            selectedRetailers,
            selectedHiddenHeader,
            options,
            isOpen,
        ],
    );

    return props;
};

export type TSectoViewer = ReturnType<typeof useSectoContainer>;
export default useSectoContainer;
